import type {
    FaqItem,
    FeatureItem,
    ServiceItem,
    StatItem
} from '@/types'

import {
    BadgeDollarSign,
    Compass,
    FileCheck2,
    Globe,
    LayoutDashboard,
    MonitorSmartphone,
    Network,
    Radar,
    ShieldCheck,
    Ship,
    Timer
} from 'lucide-react'

export type RoutePlace = {
    name: string
    lon: number
    lat: number
}

// Origin of every route drawn on the hero globe: the head office in Rio de Janeiro.
export const routeHub: RoutePlace = { name: 'Rio de Janeiro', lon: -43.18, lat: -22.9 }

// Major world trade cities, drawn without labels to picture the international freight network.
export const routeDestinations: Array<RoutePlace> = [
    { name: 'Miami', lon: -80.19, lat: 25.76 },
    { name: 'New York', lon: -74.01, lat: 40.71 },
    { name: 'Houston', lon: -95.37, lat: 29.76 },
    { name: 'Los Angeles', lon: -118.24, lat: 34.05 },
    { name: 'Mexico City', lon: -99.13, lat: 19.43 },
    { name: 'Buenos Aires', lon: -58.38, lat: -34.6 },
    { name: 'Santiago', lon: -70.67, lat: -33.45 },
    { name: 'Bogota', lon: -74.07, lat: 4.71 },
    { name: 'Lisbon', lon: -9.14, lat: 38.72 },
    { name: 'Valencia', lon: -0.38, lat: 39.47 },
    { name: 'Rotterdam', lon: 4.48, lat: 51.92 },
    { name: 'Hamburg', lon: 9.99, lat: 53.55 },
    { name: 'Genoa', lon: 8.95, lat: 44.41 },
    { name: 'Dubai', lon: 55.27, lat: 25.2 },
    { name: 'Mumbai', lon: 72.88, lat: 19.08 },
    { name: 'Durban', lon: 31.02, lat: -29.86 },
    { name: 'Lagos', lon: 3.38, lat: 6.52 },
    { name: 'Singapore', lon: 103.82, lat: 1.35 },
    { name: 'Shanghai', lon: 121.47, lat: 31.23 },
    { name: 'Hong Kong', lon: 114.17, lat: 22.32 },
    { name: 'Busan', lon: 129.08, lat: 35.18 },
    { name: 'Tokyo', lon: 139.69, lat: 35.69 }
]

export const stats: Array<StatItem> = [
    { value: 30, decimals: 0, suffix: '+', label: "Anos de atuação em comércio exterior e logística internacional" },
    { value: 4, decimals: 0, suffix: '', label: "Frentes de atuação: despacho, assessoria, logística e consultoria" },
    { value: 3, decimals: 0, suffix: '', label: "Cidades com atendimento: Rio de Janeiro, São Paulo e Santos" }
]

export const servicesIntro = "Atuamos de forma integrada no comércio exterior, oferecendo serviços especializados que garantem eficiência, segurança jurídica e previsibilidade para importadores e exportadores."

export const assessoriaItems: Array<string> = [
    "Desembaraço Aduaneiro de Importação/Exportação",
    "Entreposto Aduaneiro",
    "Admissão Temporária",
    "Exportação Temporária",
    "Drawback",
    "EX Tarifário",
    "Importação de equipamentos usados",
    "Consultoria em classificação fiscal de mercadorias"
]

export const services: Array<ServiceItem> = [
    {
        slug: 'desembaraco-aduaneiro',
        name: "Desembaraço Aduaneiro",
        summary: "Serviços especializados que garantem que todas as questões burocráticas sejam tratadas de forma eficiente e dentro das conformidades legais, com tranquilidade e segurança.",
        icon: ShieldCheck
    },
    {
        slug: 'assessoria-importacao-exportacao',
        name: "Assessoria em Importação e Exportação",
        summary: "Assessoria completa em todas as etapas da operação, da documentação à logística de transporte.",
        icon: FileCheck2,
        items: assessoriaItems
    },
    {
        slug: 'logistica-internacional',
        name: "Logística Internacional",
        summary: "Transporte, armazenagem e distribuição de mercadorias em âmbito global, com eficiência e segurança em cada etapa.",
        icon: Ship
    },
    {
        slug: 'consultoria-especializada',
        name: "Consultoria Especializada",
        summary: "Consultoria para identificar oportunidades de negócios, otimizar processos e garantir conformidade regulatória.",
        icon: Compass
    }
]

// The network and structure behind every operation (used beside the road map in the journey and on the about page).
export const reliabilityFeatures: Array<FeatureItem> = [
    {
        title: "Rede de parceiros em todo o Brasil",
        text: "Parceiros qualificados em todo o território nacional, atuando em diversos recintos aduaneiros em todos os estados brasileiros.",
        icon: Network
    },
    {
        title: "Agentes de carga no mundo",
        text: "Agentes de carga nas principais cidades do mundo, com cotações competitivas de fretes internacionais aéreos e marítimos e consolidação de cargas.",
        icon: Globe
    },
    {
        title: "Sistemas modernos, atendimento pessoal",
        text: "Sistemas de informação modernos, sem abrir mão do atendimento personalizado.",
        icon: MonitorSmartphone
    }
]

export const benefitsTitle = "Reduza custos e aumente a eficiência operacional"

export const benefits: Array<FeatureItem> = [
    {
        title: "Agilidade no Desembaraço Aduaneiro",
        text: "Questões burocráticas tratadas de forma eficiente e dentro das conformidades legais.",
        icon: Timer
    },
    {
        title: "Reduza Custos Operacionais",
        text: "Processos otimizados e oportunidades de negócio identificadas em cada operação.",
        icon: BadgeDollarSign
    },
    {
        title: "Informações Claras e Centralizadas",
        text: "Sistemas de informação modernos, sem abrir mão do atendimento personalizado.",
        icon: LayoutDashboard
    },
    {
        title: "Acompanhe Tudo em Tempo Real",
        text: "Suas operações acompanhadas de perto na Área reservada a clientes.",
        icon: Radar
    }
]

export const mission = "Facilitar e otimizar os processos de Comércio Exterior de nossos clientes, oferecendo soluções ágeis, seguras e eficientes em despacho aduaneiro e consultoria."

export const vision = "Ser referência em despacho aduaneiro e consultoria em importação e exportação no Rio de Janeiro, reconhecida pela excelência, confiabilidade e eficiência."

export const values: Array<string> = [
    "Comprometimento com resultados",
    "Confiança nas relações",
    "Transparência nas operações",
    "Eficiência e agilidade nos processos"
]

export const faqs: Array<FaqItem> = [
    {
        question: "O que a Ativa faz?",
        answer: "A Ativa atua de forma integrada no comércio exterior, com Desembaraço Aduaneiro, Assessoria em Importação e Exportação, Logística Internacional e Consultoria Especializada para importadores e exportadores."
    },
    {
        question: "Há quanto tempo a Ativa atua no comércio exterior?",
        answer: "Há mais de 30 anos, oferecendo assessoria especializada para importadores e exportadores que buscam segurança, agilidade e conformidade em suas operações."
    },
    {
        question: "Quais regimes e serviços aduaneiros vocês atendem?",
        answer: "Desembaraço Aduaneiro de Importação/Exportação, Entreposto Aduaneiro, Admissão Temporária, Exportação Temporária, Drawback, EX Tarifário, Importação de equipamentos usados e Consultoria em classificação fiscal de mercadorias."
    },
    {
        question: "A Ativa atua em todo o Brasil?",
        answer: "Sim. Contamos com uma rede de parceiros qualificados em todo o território nacional, atuando em diversos recintos aduaneiros em todos os estados brasileiros."
    },
    {
        question: "Vocês cuidam do frete internacional?",
        answer: "Sim. Temos agentes de carga nas principais cidades do mundo, com cotações competitivas de fretes internacionais aéreos e marítimos e consolidação de cargas."
    },
    {
        question: "Como acompanho minhas operações?",
        answer: "Clientes Ativa acompanham suas operações pela Área reservada a clientes, com informações claras e centralizadas."
    },
    {
        question: "Como faço uma simulação?",
        answer: "Faça uma simulação pelo nosso site ou agende uma conversa com nossos especialistas pelo formulário de contato, por telefone ou pelo WhatsApp."
    }
]

export const enquiryServiceOptions: Array<string> = [
    ...services.map((service) => service.name),
    "Ainda não sei"
]
